import React, { useState, useEffect } from 'react';
import { useAuth } from './AuthorizerContext';
import { useLocation, useNavigate } from 'react-router-dom';

interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const LoginModal: React.FC<LoginModalProps> = ({ isOpen, onClose }) => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [linkSent, setLinkSent] = useState(false);
  const { signInWithEmail, user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Reset state when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setLinkSent(false);
      setEmail('');
      setMessage('');
    }
  }, [isOpen]);

  // If user logs in while modal is open, close it and redirect to view mode
  useEffect(() => {
    if (user && isOpen) {
      // Get current path and redirect to view mode (remove /edit if present)
      const currentPath = location.pathname;
      const viewPath = currentPath.replace('/edit', '');
      onClose();
      // Only navigate if we're not already on the view path
      if (currentPath !== viewPath) {
        navigate(viewPath, { replace: true });
      }
    }
  }, [user, isOpen, location.pathname, navigate, onClose]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    try {
      // Determine redirect URL - always go to view mode (remove /edit if present)
      const currentPath = location.pathname;
      const redirectPath = currentPath.replace('/edit', '');
      
      const { error } = await signInWithEmail(email, redirectPath);
      if (error) {
        // Check if the error indicates the email doesn't exist
        const errorMessage = error.message?.toLowerCase() || '';
        const errorCode = error.status || error.code || '';
        
        // Supabase error indicators for non-existent users:
        // - "email address ... is invalid" (Supabase returns this for non-existent emails)
        // - "user not found" in message
        // - "email not found" in message
        // - status code 400 with user-related errors
        const isUserNotFound = 
          errorMessage.includes('email address') && errorMessage.includes('is invalid') ||
          errorMessage.includes('user not found') ||
          errorMessage.includes('email not found') ||
          errorMessage.includes('no user found') ||
          (errorCode === 400 && errorMessage.includes('user'));
        
        if (isUserNotFound) {
          setMessage('Sign ups aren\'t open to the public yet, but if you would like to know how to get access, check out https://patreon.com/c/SkillTreesGG');
        } else {
          setMessage(`Error: ${error.message}`);
        }
        setLinkSent(false);
      } else {
        setMessage('Check your email for a login link!');
        setEmail('');
        setLinkSent(true);
      }
    } catch (error) {
      setMessage('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Tree Editor Access</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        
        <div className="modal-body">
          {!linkSent && (
            <>
              <p>If you have an account to the tree editor, enter your email to receive a login link:</p>
              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    required
                    disabled={loading}
                    className="form-input"
                  />
                </div>
                <p className="text-muted">Skill Trees is very new! If you don't have an account, you can get early access by checking out the <a href="https://patreon.com/c/SkillTreesGG" target="_blank" rel="noopener noreferrer" className="message-link">Skill Trees Patreon</a>.</p>
                
                {message && (
                  <div className={`message ${message.includes('Error') || message.includes('Sign ups') ? 'error' : 'success'}`}>
                    {message.includes('https://patreon.com') ? (
                      <>
                        Sign ups aren't open to the public yet, but if you would like to know how to get access, check out{' '}
                        <a 
                          href="https://patreon.com/c/SkillTreesGG" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="message-link"
                        >
                          https://patreon.com/c/SkillTreesGG
                        </a>
                      </>
                    ) : (
                      message
                    )}
                  </div>
                )}
                
                <div className="form-actions">
                  <button
                    type="button"
                    onClick={onClose}
                    className="btn btn-secondary"
                    disabled={loading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={loading || !email}
                  >
                    {loading ? 'Sending...' : 'Send Login Link'}
                  </button>
                </div>
              </form>
            </>
          )}
          
          {linkSent && (
            <>
              {message && (
                <div className={`message ${message.includes('Error') || message.includes('Sign ups') ? 'error' : 'success'}`}>
                  {message.includes('https://patreon.com') ? (
                    <>
                      Sign ups aren't open to the public yet, but if you would like to know how to get access, check out{' '}
                      <a 
                        href="https://patreon.com/c/SkillTreesGG" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="message-link"
                      >
                        https://patreon.com/c/SkillTreesGG
                      </a>
                    </>
                  ) : (
                    message
                  )}
                </div>
              )}
              
              <div className="form-actions">
                <button
                  type="button"
                  onClick={onClose}
                  className="btn btn-primary"
                >
                  Okay
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
